import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import { FunnelStage } from '../../../types/reporting';
import { useTheme } from '@mui/material/styles';

interface ConversionFunnelChartProps {
  data?: FunnelStage[];
  loading?: boolean;
}

const ConversionFunnelChart: React.FC<ConversionFunnelChartProps> = ({
  data,
  loading = false,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const chartData = data?.map((stage) => ({
    name: stage.stageName,
    value: stage.jobCount,
    fill: stage.colorHex || theme.palette.primary.main,
    percentage: stage.percentage,
  })).sort((a, b) => {
    // Sort by display order if available, otherwise by value descending
    const aStage = data?.find(s => s.stageName === a.name);
    const bStage = data?.find(s => s.stageName === b.name);
    return (aStage?.displayOrder || 0) - (bStage?.displayOrder || 0);
  }) || [];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Conversion Funnel
        </Typography>
        <Box sx={{ width: '100%', height: 300 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 80, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  width={80}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 4,
                  }}
                  formatter={(value: number, name: string, entry: any) => [
                    `${value} jobs (${entry.payload.percentage}%)`,
                    'Jobs',
                  ]}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography color="text.secondary">No data available</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ConversionFunnelChart;
